/**
 * Tide-source selection: which height source and which stream source are active,
 * plus the user's Admiralty API key (their own; kept on the device, never
 * shipped). Defaults are the RYA training data so the existing training
 * behaviour is unchanged out of the box; the Real-world mode switch flips these
 * to the free FES2014 sources. See src/tides/tideSources.ts for the source model.
 */
import { create } from 'zustand';
import type { TideHeightSourceId, TideStreamSourceId } from '@/tides/tideSources';

const KEY = 'acp.tidesources.v1';

interface Persisted {
  heightSourceId: TideHeightSourceId;
  streamSourceId: TideStreamSourceId;
  admiraltyKey: string;
}

const DEFAULTS: Persisted = {
  heightSourceId: 'training',
  streamSourceId: 'training',
  admiraltyKey: '',
};

function load(): Persisted {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : null;
    return raw ? { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Persisted>) } : { ...DEFAULTS };
  } catch {
    return { ...DEFAULTS };
  }
}
function persist(p: Persisted): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}

interface TideSourceState extends Persisted {
  setHeightSource: (id: TideHeightSourceId) => void;
  setStreamSource: (id: TideStreamSourceId) => void;
  setAdmiraltyKey: (key: string) => void;
  /** True when an Admiralty key is present (the subscription sources can be used). */
  hasAdmiraltyKey: () => boolean;
}

export const useTideSourceStore = create<TideSourceState>((set, get) => ({
  ...load(),
  setHeightSource: (heightSourceId) => {
    const next = { ...current(get), heightSourceId };
    persist(next);
    set({ heightSourceId });
  },
  setStreamSource: (streamSourceId) => {
    const next = { ...current(get), streamSourceId };
    persist(next);
    set({ streamSourceId });
  },
  setAdmiraltyKey: (admiraltyKey) => {
    const next = { ...current(get), admiraltyKey };
    persist(next);
    set({ admiraltyKey });
  },
  hasAdmiraltyKey: () => get().admiraltyKey.trim().length > 0,
}));

function current(get: () => TideSourceState): Persisted {
  const s = get();
  return { heightSourceId: s.heightSourceId, streamSourceId: s.streamSourceId, admiraltyKey: s.admiraltyKey };
}
